import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, FileText, XCircle } from 'lucide-react';

import NavigationBar from '../components/NavigationBar';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import apiService from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { shouldShowBusinessDashboard } from '../utils/navigationUtils';

const BusinessChargeInvoiceCompletePage: React.FC = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const paymentIntentId = searchParams.get('payment_intent') || '';
  const [status, setStatus] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(true);

  useEffect(() => {
    if (!invoiceId || !paymentIntentId) {
      setSyncing(false);
      return;
    }
    (async () => {
      try {
        const resp = await apiService.syncInvoicePaymentIntent(invoiceId, paymentIntentId);
        setStatus(resp.mappedStatus || resp.status);
      } catch (e: any) {
        console.error('Payment sync failed', e);
      } finally {
        setSyncing(false);
      }
    })();
  }, [invoiceId, paymentIntentId]);

  const isSuccess = status ? ['SUCCEEDED', 'SUCCEED', 'SUCCESS'].includes(String(status).toUpperCase()) : false;
  const isFailed = status ? ['FAILED', 'CANCELED', 'CANCELLED', 'REQUIRES_PAYMENT_METHOD'].includes(String(status).toUpperCase()) : false;

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  if (!invoiceId) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Found</h1>
            <button onClick={() => navigate('/dashboard/business?section=invoices')} className="aacore-btn2-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Invoices
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NavigationBar />
      <div className="flex-1 lg:grid lg:grid-cols-[auto_1fr]">
        {shouldShowBusinessDashboard(user!) && (
          <BusinessDashboardSidebar
            isSidebarOpen={isSidebarOpen}
            isSidebarCollapsed={isSidebarCollapsed}
            activeSection="invoices"
            onClose={() => setIsSidebarOpen(false)}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            unreadNotificationCount={0}
          />
        )}
        <div className="flex-1 p-4 md:p-8 flex items-center justify-center">
          <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 p-6 text-center">
            {syncing ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B4778] mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-900 mb-2">Confirming payment</h1>
                <p className="text-gray-600 text-sm">Please wait…</p>
              </>
            ) : isSuccess ? (
              <>
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment successful</h1>
                <p className="text-gray-600 mb-6">
                  The invoice has been marked as paid. A copy of the paid invoice has been sent to the receipt email.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <button
                    onClick={() => navigate(`/dashboard/business/invoices/${invoiceId}`)}
                    className="aacore-btn2-primary inline-flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    View invoice
                  </button>
                  <button
                    onClick={() => navigate('/dashboard/business?section=invoices')}
                    className="aacore-btn2-outline inline-flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Invoices
                  </button>
                </div>
              </>
            ) : isFailed ? (
              <>
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment not completed</h1>
                <p className="text-gray-600 mb-6">The charge did not succeed. You can try again from the invoice.</p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <button
                    onClick={() => navigate(`/dashboard/business/invoices/${invoiceId}/charge`)}
                    className="aacore-btn2-primary inline-flex items-center justify-center gap-2"
                  >
                    Try again
                  </button>
                  <button
                    onClick={() => navigate('/dashboard/business?section=invoices')}
                    className="aacore-btn2-outline inline-flex items-center justify-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Invoices
                  </button>
                </div>
              </>
            ) : (
              <>
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment status</h1>
                <p className="text-gray-600 mb-6">Status: {status || 'Unknown'}</p>
                <button
                  onClick={() => navigate(`/dashboard/business/invoices/${invoiceId}`)}
                  className="aacore-btn2-primary inline-flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  View invoice
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessChargeInvoiceCompletePage;
